import {logger} from 'common'
import {useTranslations} from 'next-intl'
import {ReactNode} from 'react'

import {sanitizeKeysOfValues} from './values'

const defaultComponents = {
  strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
  b: (chunks: React.ReactNode) => <b>{chunks}</b>,
  em: (chunks: React.ReactNode) => <em>{chunks}</em>,
  i: (chunks: React.ReactNode) => <i>{chunks}</i>,
  p: (chunks: React.ReactNode) => <p>{chunks}</p>,
  br: () => <br />,
  ul: (chunks: React.ReactNode) => <ul>{chunks}</ul>,
  li: (chunks: React.ReactNode) => <li>{chunks}</li>,
  span: (chunks: React.ReactNode) => <span>{chunks}</span>,
}

export type TFunction = {
  (key: string | undefined, values?: Record<string, any>): string
  // Whether a key resolves, without the "missing translation" warning t() logs —
  // for telling a stored translation key apart from free text.
  has: (key: string | undefined) => boolean
  rich?: (key: string | undefined, values?: Record<string, any>) => ReactNode
}

// If namespace is omitted, then keys WITH any namespace can be used, while keys WITHOUT namespace will be considered from 'common'
// If namespace is specified, it is considered to be a default namespace for keys without namespace; still fallbacking to 'common'
// Keys missing from 'common' finally fall back to 'server' (invoice/PDF/email/SMS strings). Pages never load that
// namespace, so it only resolves where the full message set is provided — e.g. the PDF/HTML document renderers.
export function useTranslation(namespace?: string) {
  const allT = useTranslations()

  const t = (key: string | undefined, values?: Record<string, any>) => {
    if (!key) {
      return ''
    }

    // namespace can be passed as 'ns' value (in values)
    const _key =
      values && 'ns' in values && !key.startsWith(`${values['ns']}.`)
        ? `${values['ns']}.${key}`
        : key

    const _values = values ? sanitizeKeysOfValues(values) : undefined

    if (_key.includes('.') && allT.has(_key)) {
      // namespace IS specified in the 'key'
      return allT(_key, _values)
    }

    // namespace IS NOT specified in the 'key'

    if (namespace && allT.has(`${namespace}.${_key}`)) {
      return allT(`${namespace}.${_key}`, _values)
    }
    if (allT.has(`common.${_key}`)) {
      return allT(`common.${_key}`, _values)
    }
    if (allT.has(`server.${_key}`)) {
      return allT(`server.${_key}`, _values)
    }

    logger.warn(
      `[useTranslations.t] Missing translation key ${_key}` +
        (namespace ? `, namespace: ${namespace}` : ''),
    )
    return _key
  }

  t.has = (key: string | undefined) => {
    if (!key) {
      return false
    }

    return Boolean(
      (key.includes('.') && allT.has(key)) ||
        (namespace && allT.has(`${namespace}.${key}`)) ||
        allT.has(`common.${key}`) ||
        allT.has(`server.${key}`),
    )
  }

  t.rich = (
    key: string | undefined,
    components?: Record<
      string,
      string | number | null | undefined | ((chunks: React.ReactNode) => React.ReactNode)
    >,
  ) => {
    if (!key) {
      return ''
    }

    // namespace can be passed as 'ns' value (in values)
    const _key =
      components && 'ns' in components && !key.startsWith(`${components['ns']}.`)
        ? `${components['ns']}.${key}`
        : key

    const _components = {
      ...defaultComponents,
      ...components, // custom components override defaults
    }

    if (_key.includes('.') && allT.has(_key)) {
      // namespace IS  specified in the 'key'
      return allT.rich(_key, _components)
    }

    // namespace IS NOT specified in the 'key'

    if (namespace && allT.has(`${namespace}.${_key}`)) {
      return allT.rich(`${namespace}.${_key}`, _components)
    }
    if (allT.has(`common.${_key}`)) {
      return allT.rich(`common.${_key}`, _components)
    }
    if (allT.has(`server.${_key}`)) {
      return allT.rich(`server.${_key}`, _components)
    }

    logger.warn(
      `[useTranslations.rich] Missing translation key ${_key}` +
        (namespace ? `, namespace: ${namespace}` : ''),
    )
    return _key
  }

  return {t}
}
